import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PartnersSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [desktopOffset, setDesktopOffset] = useState(0);
  
  const partners = [
    { name: 'GYM24', logo: '/images/partner-gym24-logo.png' },
    { name: 'Leaders Unified', logo: '/images/partner-leaders-unified-logo.png' },
    { name: '2A Sports Lab', logo: '/images/partner-2a-sports-lab-logo.png' },
    { name: 'JD Coaching', logo: '/images/partner-jd-coaching-logo.png' },
    { name: 'Ellavita', logo: '/images/partner-ellavita-logo.png' },
    { name: 'Ples Plus', logo: '/images/partner-ples-plus-logo.png' },
    { name: 'Dober Trening', logo: '/images/partner-dober-trening-logo.png' },
  ];

  const mobileSlides = Math.ceil(partners.length / 3);
  
  // Desktop infinite scroll animation
  useEffect(() => {
    const timer = setInterval(() => {
      setDesktopOffset((prev) => prev + 1);
    }, 50);
    return () => clearInterval(timer);
  }, []);

  // Mobile auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mobileSlides);
    }, 4000);
    return () => clearInterval(timer);
  }, [mobileSlides]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % mobileSlides);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + mobileSlides) % mobileSlides);

  // Double the partners for infinite scroll effect
  const doubledPartners = [...partners, ...partners];

  return (
    <section 
      id="partnerji" 
      className="py-10 bg-slate-50 overflow-hidden"
      data-testid="partners-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
            Zaupajo nam
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mb-4">
            Sodelujemo z
          </h2>
        </div>

        {/* Desktop: Infinite scrolling carousel */}
        <div className="hidden md:block relative">
          <div className="overflow-hidden">
            <div 
              className="flex gap-6 transition-none"
              style={{ 
                transform: `translateX(-${desktopOffset % (partners.length * 176)}px)`,
              }}
            >
              {doubledPartners.map((partner, index) => (
                <div
                  key={index}
                  className={`flex-shrink-0 flex items-center justify-center h-28 w-40 rounded-2xl p-4 shadow-sm hover:shadow-lg transition-shadow group overflow-hidden ${partner.darkBg ? 'bg-[#1a1a1a]' : 'bg-white'}`}
                >
                  <img loading="lazy" decoding="async"
                    src={partner.logo}
                    alt={`Partner ${partner.name} fizioterapija`}
                    className="h-20 w-auto object-contain transition-all opacity-90 group-hover:opacity-100"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-sm font-bold ${partner.darkBg ? 'text-white' : 'text-slate-400'}">${partner.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Gradient fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-50 to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none" />
        </div>

        {/* Mobile: 3 at a time slider */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: mobileSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="w-full flex-shrink-0 flex justify-center gap-3 px-2">
                  {partners.slice(slideIndex * 3, slideIndex * 3 + 3).map((partner, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-center h-24 w-[30%] rounded-xl p-2 shadow-sm ${partner.darkBg ? 'bg-[#111]' : 'bg-white'}`}
                    >
                      <img loading="lazy" decoding="async"
                        src={partner.logo}
                        alt={`Partner ${partner.name} fizioterapija`}
                        className={`h-16 w-auto object-contain ${partner.darkBg ? '' : ''}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-xs font-bold ${partner.darkBg ? 'text-white' : 'text-slate-400'}">${partner.name}</span>`;
                        }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          {/* Navigation arrows */}
          <button 
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-slate-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-slate-600"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: mobileSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide ? 'bg-[#f87171] w-6' : 'bg-slate-300 w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
